import { useState, useEffect, useCallback, RefObject } from 'react';

const isOverflown = (element: HTMLElement): boolean => {
  return element.scrollWidth > element.clientWidth;
};

export const useTextTruncation = (
  refs: RefObject<(HTMLElement | null)[]>,
  options: unknown[]
): boolean[] => {
  const [truncatedIndices, setTruncatedIndices] = useState<boolean[]>(
    new Array(options.length).fill(false)
  );

  const measureTruncation = useCallback(() => {
    if (!refs.current) return;

    const newTruncatedIndices = options.map((_, index) => {
      const labelElement = refs.current ? refs.current[index] : null;
      if (labelElement instanceof HTMLElement) {
        return isOverflown(labelElement);
      }
      return false;
    });
    setTruncatedIndices(newTruncatedIndices);
  }, [options, refs]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(measureTruncation);
    const mutationObserver = new MutationObserver(measureTruncation);

    if (refs.current) {
      refs.current.forEach((element) => {
        if (element) {
          resizeObserver.observe(element);
          mutationObserver.observe(element, {
            childList: true,
            subtree: true,
          });
        }
      });
    }

    measureTruncation();

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [measureTruncation, refs]);

  return truncatedIndices;
};

export default useTextTruncation;
